// src/components/workflows/nodes/SendSMSNode.jsx - Send SMS Action Node
import React, { useState, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { MessageSquare, Edit2, Flag } from 'lucide-react';
import { useSettings } from '../../../hooks/useSettings';
import { getAvailableEntities } from '../../../config/workflowEntities';

// Helper to extract variables from template content
const extractVariables = (messageBody = '') => {
  const matches = messageBody.match(/\{\{([^}]+)\}\}/g) || [];
  return matches.map(m => m.replace(/\{\{|\}\}/g, '').trim());
};

// Helper to check if template variables are available
const areVariablesAvailable = (variables, availableEntities) => {
  if (variables.length === 0) return true; // No variables needed

  // Map variable prefixes to entity types
  const variableEntityMap = {
    'customer': 'customer',
    'appointment': 'appointment',
    'event': 'appointment',
    'service': 'appointment',
    'invoice': 'invoice'
  };

  // Check if all variables are available
  return variables.every(variable => {
    const prefix = variable.split('.')[0].toLowerCase();
    const requiredEntity = variableEntityMap[prefix];

    // If we don't know the entity, assume it's available (business info, etc.)
    if (!requiredEntity) return true;

    return availableEntities.includes(requiredEntity);
  });
};

/**
 * SendSMSNode - Represents an SMS sending action
 *
 * Node data structure (BACKEND-ALIGNED):
 * {
 *   id: 'sms-uuid',
 *   type: 'action',            // ← Changed from 'sendSMS'
 *   data: {
 *     actionType: 'send_sms',  // ← Backend checks this
 *     config: {                // ← Backend expects config object
 *       templateId: 'template-uuid' // ← NEW: Reference to SMS template
 *     },
 *     label: 'Send Reminder SMS',
 *     description: 'Send appointment reminder'
 *   }
 * }
 */
const SendSMSNode = ({ data, selected, id }) => {
  const {
    actionType = 'send_sms',
    config = {},
    label,
    description,
    isStageGate = false,
    stageName = '',
    stageColor = 'green'
  } = data;
  const { updateNodeData, getNodes } = useReactFlow();
  const { smsTemplates } = useSettings();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');

  // Get available entities based on workflow entry node
  const availableEntities = useMemo(() => {
    const nodes = getNodes();
    return getAvailableEntities(nodes, id);
  }, [getNodes, id]);

  // Filter and categorize templates
  const { availableTemplates, unavailableTemplates } = useMemo(() => {
    const active = smsTemplates.filter(t => t.active);

    const available = [];
    const unavailable = [];

    active.forEach(template => {
      const variables = extractVariables(template.message_body);
      const isAvailable = areVariablesAvailable(variables, availableEntities);

      if (isAvailable) {
        available.push(template);
      } else {
        unavailable.push(template);
      }
    });

    return { availableTemplates: available, unavailableTemplates: unavailable };
  }, [smsTemplates, availableEntities]);

  // Handle template selection
  const handleTemplateChange = (templateId) => {
    updateNodeData(id, {
      ...data,
      config: { ...config, templateId }
    });
  };

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  return (
    <>
      {/* Input handle (left side) */}
      <Handle isConnectable={1}
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-green-500"
      />

      <div className={`
        w-64 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-green-500 shadow-lg border-green-500/50' : 'shadow-md border-green-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-green-500/10 to-green-600/5
        relative
      `}>
        {/* Stage gate indicator flag */}
        {isStageGate && (
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center shadow-lg z-10 border-2 border-slate-950">
            <Flag className="w-3.5 h-3.5 text-white" />
          </div>
        )}

        {/* Header */}
        <div className="p-3 pb-2 border-b border-green-500/10">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-green-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              Send SMS
            </div>
            <Badge variant="outline" className="text-xs border-green-500/50 text-green-300">
              SMS
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2">
          {/* Template Selection */}
          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-medium">SMS Template:</label>
            <Select
              value={config.templateId || ''}
              onValueChange={handleTemplateChange}
            >
              <SelectTrigger className="w-full bg-green-500/10 border-green-500/30 text-green-200 text-sm nodrag">
                <SelectValue placeholder="Select SMS template..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {availableTemplates.length === 0 && unavailableTemplates.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-slate-400 italic">
                    No active SMS templates
                  </div>
                )}

                {/* Available templates */}
                {availableTemplates.map(template => (
                  <SelectItem
                    key={template.id}
                    value={template.id}
                    className="text-slate-200"
                  >
                    {template.name}
                  </SelectItem>
                ))}

                {/* Unavailable templates (greyed out) */}
                {unavailableTemplates.map(template => (
                  <SelectItem
                    key={template.id}
                    value={template.id}
                    className="text-slate-500 opacity-50"
                    disabled
                  >
                    {template.name} (missing data)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Editable Description */}
          <div className="border-t border-green-500/10 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') {
                    setEditedDescription(description || '');
                    setIsEditingDescription(false);
                  }
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 nodrag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer nodrag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic text-center">
                    {description}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    Add description...
                  </div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-green-400" />
              </div>
            )}
          </div>

          {/* Stage Gate Toggle Section */}
          <div className="border-t border-green-500/10 pt-3">
            <label className="flex items-center gap-2 cursor-pointer nodrag">
              <input
                type="checkbox"
                checked={isStageGate}
                onChange={(e) => updateNodeData(id, {
                  ...data,
                  isStageGate: e.target.checked,
                  stageName: e.target.checked && !stageName ? 'New Stage' : stageName
                })}
                className="w-4 h-4 text-purple-500 bg-slate-800 border-slate-600 rounded focus:ring-purple-500"
              />
              <span className="text-xs text-slate-300 font-medium">Make this a stage gate</span>
            </label>

            {isStageGate && (
              <div className="mt-2 space-y-2 nodrag">
                <Input
                  placeholder="Stage name (e.g., 'SMS Sent')"
                  value={stageName}
                  onChange={(e) => updateNodeData(id, {
                    ...data,
                    stageName: e.target.value
                  })}
                  className="text-xs bg-slate-800/50 border-slate-700 text-slate-200"
                />
                <Select
                  value={stageColor}
                  onValueChange={(color) => updateNodeData(id, {
                    ...data,
                    stageColor: color
                  })}
                >
                  <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="purple" className="text-slate-200">Purple</SelectItem>
                    <SelectItem value="blue" className="text-slate-200">Blue</SelectItem>
                    <SelectItem value="green" className="text-slate-200">Green</SelectItem>
                    <SelectItem value="orange" className="text-slate-200">Orange</SelectItem>
                    <SelectItem value="red" className="text-slate-200">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Output handle (right side) */}
      <Handle isConnectable={1}
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-green-500"
      />
    </>
  );
};

export default SendSMSNode;
